import * as THREE from "three";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import {
  ANIMATIONS,
  loadAnimation,
  loadCharacter,
  pickCharacterForPlayer,
  prefetchAllAnimations,
  type AnimationId,
} from "./characterModels.js";

const CAPSULE_RADIUS = 0.4;
const CAPSULE_HEIGHT = 1.8;
const FADE_DURATION = 0.18; // crossfade between clips

/**
 * Wraps the visual representation of a player: a colored capsule placeholder
 * shown immediately, replaced by a skinned GLTF character + animation mixer
 * once the model loads. Same class used by LocalPlayer and RemotePlayer.
 */
export class PlayerVisual {
  readonly group: THREE.Group;

  private capsule: THREE.Mesh;
  private color: string;
  private model: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private actions: Map<AnimationId, THREE.AnimationAction> = new Map();
  private currentAction: THREE.AnimationAction | null = null;
  private currentAnim: AnimationId | null = null;

  /** Wall-clock ms at which the current one-shot animation should release back
   *  to the movement-driven state machine. 0 = no one-shot is locking. */
  private oneShotLockUntil: number = 0;

  /** Most recent normalized XZ run direction. Used to detect sharp turns and
   *  trigger the change_direction animation. Null when not currently running. */
  private lastMoveDir: { x: number; z: number } | null = null;

  constructor(color: string) {
    this.color = color;
    this.group = new THREE.Group();

    const geom = new THREE.CapsuleGeometry(
      CAPSULE_RADIUS,
      CAPSULE_HEIGHT - 2 * CAPSULE_RADIUS,
      6,
      12,
    );
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.6 });
    this.capsule = new THREE.Mesh(geom, mat);
    this.capsule.position.y = CAPSULE_HEIGHT / 2;
    this.capsule.castShadow = true;
    this.group.add(this.capsule);
  }

  setColor(color: string) {
    if (color === this.color) return;
    this.color = color;
    (this.capsule.material as THREE.MeshStandardMaterial).color.set(color);
  }

  /**
   * Kick off async load of the character + all animations. The capsule remains
   * visible until everything is ready; then it's swapped for the skinned model
   * and the idle animation starts.
   */
  async loadCharacter(playerId: string): Promise<void> {
    const charId = pickCharacterForPlayer(playerId);
    const [gltf, _anims] = await Promise.all([
      loadCharacter(charId),
      prefetchAllAnimations(),
    ]);

    // Clone the scene so multiple players using the same character don't share state.
    const cloned = SkeletonUtils.clone(gltf.scene);

    // Measure at scale=1 to find the natural rendered height, then scale to
    // match CAPSULE_HEIGHT. Empirically these gltf.report Mixamo files render
    // at ~2m natural — but we measure rather than hardcode so it works for any
    // character.
    cloned.scale.setScalar(1);
    cloned.updateMatrixWorld(true);
    const naturalBbox = new THREE.Box3().setFromObject(cloned);
    const naturalHeight = Math.max(naturalBbox.max.y - naturalBbox.min.y, 1e-3);
    const scale = CAPSULE_HEIGHT / naturalHeight;
    cloned.scale.setScalar(scale);
    cloned.updateMatrixWorld(true);

    const finalBbox = new THREE.Box3().setFromObject(cloned);
    const finalHeight = finalBbox.max.y - finalBbox.min.y;
    console.log(
      `[PlayerVisual] ${charId} natural=${naturalHeight.toFixed(2)}m  ` +
        `scale=${scale.toFixed(3)}  final=${finalHeight.toFixed(2)}m`,
    );

    // Offset so feet sit at y=0 inside the player's group regardless of where
    // the model's natural origin is.
    cloned.position.y = -finalBbox.min.y;

    // Mixamo characters export facing +Z; Three.js convention is -Z forward.
    // Rotate 180° around Y so the character looks the same direction the
    // player's yaw points.
    cloned.rotation.y = Math.PI;

    cloned.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.SkinnedMesh) {
        obj.castShadow = true;
        obj.receiveShadow = false;
      }
    });

    // Remove the capsule placeholder and add the model.
    this.group.remove(this.capsule);
    this.capsule.geometry.dispose();
    (this.capsule.material as THREE.Material).dispose();

    this.group.add(cloned);
    this.model = cloned;

    // Set up animation mixer + actions.
    this.mixer = new THREE.AnimationMixer(cloned);
    for (const animId of ANIMATIONS) {
      const clip = await loadAnimation(animId);
      const action = this.mixer.clipAction(clip);
      this.actions.set(animId, action);
    }

    // Start in idle.
    this.play("idle");
  }

  /** Crossfade to the given animation. */
  play(animId: AnimationId, opts?: { loop?: boolean; reset?: boolean }) {
    if (!this.mixer) return;
    if (this.currentAnim === animId && !opts?.reset) return;
    const next = this.actions.get(animId);
    if (!next) return;

    const loop = opts?.loop ?? true;
    next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    next.clampWhenFinished = !loop;

    if (this.currentAction && this.currentAction !== next) {
      // Reset the incoming action to t=0, then crossfade.
      next.reset();
      next.enabled = true;
      next.setEffectiveTimeScale(1);
      next.setEffectiveWeight(1);
      this.currentAction.crossFadeTo(next, FADE_DURATION, false);
      next.play();
    } else {
      if (opts?.reset) next.reset();
      next.enabled = true;
      next.setEffectiveTimeScale(1);
      next.setEffectiveWeight(1);
      next.play();
    }

    this.currentAction = next;
    this.currentAnim = animId;
  }

  update(dt: number) {
    if (this.mixer) this.mixer.update(dt);
  }

  /**
   * Play a non-looping animation once. Locks the movement state machine for
   * `durationMs` (or the clip's duration if not provided) so it doesn't get
   * crossfaded back to idle/run mid-kick.
   */
  playOnce(animId: AnimationId, durationMs?: number) {
    const action = this.actions.get(animId);
    if (!action) return;
    this.play(animId, { loop: false, reset: true });
    const clipDur = action.getClip().duration * 1000;
    this.oneShotLockUntil = performance.now() + (durationMs ?? clipDur);
  }

  /**
   * Pick the right animation based on movement state.
   *
   * Jump plays once, then clamps on its last frame until the player lands.
   * This matches the "freeze in last pose while falling" feel.
   *
   * A `playOnce` lock (e.g. for a kick) suppresses movement-driven changes
   * until the one-shot completes.
   */
  applyMovementState(state: { vel: [number, number, number]; grounded: boolean }) {
    if (performance.now() < this.oneShotLockUntil) return;
    const horizontalSpeed = Math.hypot(state.vel[0], state.vel[2]);
    if (!state.grounded) {
      // Loop=false + clampWhenFinished=true (set by play()) holds the last frame.
      this.play("jumping", { loop: false });
      this.lastMoveDir = null;
    } else if (horizontalSpeed > 0.2) {
      const curDir = { x: state.vel[0] / horizontalSpeed, z: state.vel[2] / horizontalSpeed };
      if (this.lastMoveDir) {
        const dot = curDir.x * this.lastMoveDir.x + curDir.z * this.lastMoveDir.z;
        // Sharp turn (~> 70°): play change_direction once, then crossfade into
        // running on lock release.
        if (dot < 0.3) {
          this.playOnce("change_direction");
          this.lastMoveDir = curDir;
          return;
        }
      }
      this.play("running_forwards");
      this.lastMoveDir = curDir;
    } else {
      this.play("idle");
      this.lastMoveDir = null;
    }
  }

  dispose() {
    if (this.model) {
      this.model.traverse((obj) => {
        if (obj instanceof THREE.SkinnedMesh || obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material as THREE.Material | THREE.Material[];
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat.dispose();
        }
      });
    } else {
      this.capsule.geometry.dispose();
      (this.capsule.material as THREE.Material).dispose();
    }
    this.actions.clear();
    this.mixer = null;
  }
}
